<template>
    <div class="date-picker--container">
        <date-picker
            v-model="date"
            class="custom-calendar"
            is-expanded
            :min-date="earliestDate"
            :max-date="maxDate"
            :disabled-dates='disabledDatesFromAPI'
            :attributes='getAttributes'
            :from-date="date"
            :first-day-of-week="2"/>

        <div v-if="isLowDateSelected && !hasMultipleDeliveriesSelected" class="slot-info">
            <p>{{ $t('PICKED_LOW_AVAILABLE_DAY')}}</p>
        </div>

        <div v-if="!hasMultipleDeliveriesSelected" class="no-slot-info">
            <div class="text-section">
                <h6>{{ $t('DATE_NOT_AVAILABLE')}}</h6>
                <p>{{ $t('NOT_AVAILABLE_TEXT')}}</p>
            </div>
            <div class="icon-section">
                <img src="@/assets/images/decoration/stem.svg" alt="stem-icon">
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { DatePicker } from 'v-calendar';
import {
    addHours,
    addDays,
    addYears,
    formatDate,
    getHoursFromDate,
    getDayFromDate,
    getStartOfDay,
    getUTCDateTime,
    isDateAfter,
    isSameDate,
} from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import { PRODUCT_TYPE_BUMPER_MULTI, PRODUCT_TYPE_BUMPER_SINGLE } from '@/constants/Gifting.js';

export default {
    name: 'DeliveryDay',
    components: {
        DatePicker,
    },
    data() {
        return {
            date: null,
            allowedDates: null,
            disabledDates: [0, 6],
            now: new Date(),
            oneOffGiftsDisabledDates: [
                '2020-04-10',
                '2020-04-13',
            ],
            earliestDate: null,
            maxDate: null,
            isLowDateSelected: false,
        };
    },
    mounted() {
        this.showCalendar();
        this.$store.dispatch('cms/fetchGiftingUndeliverableDates', true);

        setTimeout(() => {
            this.showCalendar();
        }, 1000);

        // Recalculate the dates every 15 seconds in the instance the cut off time has passed.
        setInterval(this.populateDates, 10000);
    },
    computed: {
        ...mapGetters({
            deliverableDate: 'gifting/getDeliverableDate',
            selectedBoxName: 'gifting/getSelectedBoxName',
            hasMultipleDeliveriesSelected: 'gifting/isSubscription',
            allUndeliverableDates: 'cms/getGiftingUndeliverableDates',
            selectedOccassion: 'specialDates/getSelectedOccasion',
            deliveryDates: 'gifting/getGiftDeliveryDates',
        }),

        deliveryDay: {
            get() {
                return this.$store.getters['gifting/getDeliveryDateSelected'];
            },
            set(value) {
                this.$store.commit('gifting/setDeliveryDateSelected', value);
            },
        },

        undeliverableDates() {
            let dates = (this.selectedBoxName === PRODUCT_TYPE_BUMPER_MULTI) || (this.selectedBoxName === PRODUCT_TYPE_BUMPER_SINGLE)
                ? this.allUndeliverableDates?.bumper : this.allUndeliverableDates?.data;

            return dates?.map(date => {
                return formatDate(date.date, Dates.DASHED_YEAR_MONTH_DAY);
            });
        },

        deliverableDatesForSubscription() {
            return [1, 2, 3, 4, 5, 6, 7].filter(date => date !== (this.deliverableDate + 1));
        },

        undeliverableDatesForOneOffGifts() {
            this.undeliverableDates?.concat(this.oneOffGiftsDisabledDates.map(dateStr => formatDate(dateStr, Dates.DASHED_YEAR_MONTH_DAY)));
            return this.undeliverableDates?.concat(this.getNotDeliverableDates);
        },

        getHighDates() {
            let filteredDates = this.deliveryDates.filter(item => item.is_high);
            let highAvaiableDates = [];
            filteredDates.map(item => {
                highAvaiableDates.push(item.gift_date);
            });
            return highAvaiableDates.filter(item => isDateAfter(item, this.earliestDate) || isSameDate(item, this.earliestDate));
        },

        getLowDates() {
            let filteredDates = this.deliveryDates.filter(item => item.is_low);
            let lowAvaiableDates = [];
            filteredDates.map(item => {
                lowAvaiableDates.push(item.gift_date);
            });
            return lowAvaiableDates.filter(item => isDateAfter(item, this.earliestDate) || isSameDate(item, this.earliestDate));
        },

        getNotDeliverableDates() {
            let dates = this.deliveryDates.filter(item => item.is_not);
            return dates.map(item => item.gift_date);
        },

        getAttributes() {
            return this.hasMultipleDeliveriesSelected
                ? [
                    {
                        key: 'selectedDate',
                        highlight: {
                            style: {
                                height: '32px',
                                width: '32px',
                                border: '2px solid black',
                            },
                            contentStyle: {
                            },
                        },
                        dates: this.date,
                    },
                ] : [
                    {
                        key: 'selectedDate',
                        highlight: {
                            style: {
                                height: '32px',
                                width: '32px',
                                border: '2px solid black',
                            },
                            contentStyle: {
                            },
                        },
                        dates: this.date,
                    },
                    {
                        key: 'high',
                        highlight: {
                            style: {
                                background: '#64A667',
                                height: '32px',
                                width: '32px',
                                opacity: 0.2,
                            },
                            contentStyle: {
                            },
                        },
                        dates: this.getHighDates,
                    },
                    {
                        key: 'low',
                        highlight: {
                            style: {
                                background: '#F5B50E',
                                height: '32px',
                                width: '32px',
                                opacity: 0.2,
                            },
                            contentStyle: {
                            },
                        },
                        dates: this.getLowDates,
                    },
                ];
        },

        disabledDatesFromAPI() {
            let disabled = (this.hasMultipleDeliveriesSelected ? this.undeliverableDates : this.undeliverableDatesForOneOffGifts) || [];
            return this.hasMultipleDeliveriesSelected ? [...disabled, { weekdays: this.deliverableDatesForSubscription }] : [...disabled, { weekdays: [1, 7] }];
        },
    },
    methods: {
        showCalendar() {
            this.populateDates();
            this.getInitialDate();
        },

        populateDates() {
            this.$store.dispatch('cms/fetchGiftingUndeliverableDates', false);

            if (this.hasMultipleDeliveriesSelected) {
                this.disabledDates = this.deliverableDatesForSubscription;
            }

            this.earliestDate = getStartOfDay(this.now); // disabled dates before this date (today)
            this.maxDate = getStartOfDay(addYears(this.now, 2)); // disable dates after 2 years from now

            this.earliestDate = getHoursFromDate(this.now) > 11 ? addDays(this.earliestDate, 3) : addDays(this.earliestDate, 2);
            this.allowedDates = {
                from: this.maxDate, // Disable all dates after specific date
                to: this.earliestDate, // Disable all dates up to specific date
                days: this.disabledDates, // [0, 6] Disable Sunday's and Saturday's
                dates: this.hasMultipleDeliveriesSelected ? this.undeliverableDates : this.undeliverableDatesForOneOffGifts, // Disable an array of dates
            };
        },

        getInitialDate() {
            if (this.selectedOccassion) {
                let reminderDate = this.selectedOccassion?.date;
                let isReminderDateDeliverable = this.isDeliverableDate(reminderDate);
                if (isReminderDateDeliverable) {
                    return this.date = reminderDate;
                } else {
                    let nearestDate = this.hasMultipleDeliveriesSelected ? addDays(reminderDate, -4) : addDays(reminderDate, -2);
                    return this.date = formatDate(nearestDate, Dates.DASHED_YEAR_MONTH_DAY);
                }
            }
            if (this.deliveryDay && this.isDeliverableDate(new Date(this.deliveryDay))) {
                this.date = new Date(this.deliveryDay);
            } else {
                let proposedDate = getStartOfDay(this.now);
                let iterations = 0;
                let maxIterations = 1000;

                while (iterations < maxIterations) {
                    // Check date is after the allowed cut off time and a day we deliver
                    if (this.isDeliverableDate(proposedDate)) {
                        this.date = proposedDate;
                        break;
                    }

                    proposedDate = addDays(proposedDate, 1);
                    iterations++;
                }
            }
        },

        isDeliverableDate(dateStr) {
            let dateNumber = this.hasMultipleDeliveriesSelected ? getDayFromDate(dateStr) + 1 : getDayFromDate(dateStr);
            let notWeekendDate = !this.allowedDates.days.includes(dateNumber);
            let isDisabledDateOneOff = this.undeliverableDatesForOneOffGifts?.includes(formatDate(dateStr, Dates.DASHED_YEAR_MONTH_DAY));
            if (isSameDate(dateStr, this.allowedDates.to) && !this.hasMultipleDeliveriesSelected && notWeekendDate && !isDisabledDateOneOff) return true;
            if (!isDateAfter(dateStr, this.allowedDates.to) || this.oneOffGiftsDisabledDates.includes(formatDate(dateStr, Dates.DASHED_YEAR_MONTH_DAY))) {
                return false;
            }

            let isUndeliverableDate = this.undeliverableDates?.find(dtStr => {
                return isSameDate(getStartOfDay(dtStr), dateStr);
            });

            return isUndeliverableDate ? false : notWeekendDate;
        },

        dateSelected() {
            this.$scrollTo('.scroll-to-text-me', {
                offset: -80,
            });
        },
    },
    watch: {
        date(value) {
            let localeDate = formatDate(new Date(value), Dates.DASHED_YEAR_MONTH_DAY);
            this.isLowDateSelected = this.getLowDates.includes(localeDate);
            this.deliveryDay = this.date;
        },
    },
};
</script>

<style lang="scss" scoped>
    ::v-deep .custom-calendar.vc-container {
        --text-color: #262323;
        --disabled-color: #B7B6B6;
        --border-color: #CECECE;
        --font-family: 'Gill Sans';
        --font-weight: 300;

        border-radius: 0;
        border: 1px solid var(--border-color);

        & .vc-header {
            pointer-events: none;
            margin-top: 6px;
            margin-bottom: 16px;
        }

        & .vc-arrow {
            margin-top: 6px;
        }

        & .vc-svg-icon {
            color: var(--text-color);
        }

        & .vc-weeks {
            padding: 4px 24px;
            @media (max-width: 768px) {
                padding: 4px 8px;
            }
        }

        & .vc-weekday {
            font-weight: 400;
            font-size: 18px;
            font-family: var(--font-family);
            color: var(--text-color);
        }

        & .vc-title {
            font-weight: 400;
            font-size: 18px;
            font-family: var(--font-family);
            color: var(--text-color);
        }

        & .vc-day-content {
            border-radius: 50%;
            font-weight: var(--font-weight) !important;
            font-size: 18px;
            line-height: 24px;
            font-family: var(--font-family);
        }

        & .is-disabled {
            pointer-events: none;
            color: var(--disabled-color);
        }

        & .vc-day {
            margin-bottom: 4px;
            margin-top: 4px;
        }
    }
    .date-picker {
        &--container {
            margin: 24px 0px;
        }
    }
    .slot-info {
        max-width: 400px;
        border-radius: 8px;
        background: rgba(245, 181, 14, 0.2);
        border: 2px solid #F5B50E;
        margin-top: 16px;
        text-align: center;
    }
    .no-slot-info {
        display: flex;
        flex-direction: row;
        background: #ECE9E8;
        border-radius: 8px;
        padding: 24px 32px;
        margin-top: 16px;
    }
    .text-section {
        width: 80%;
        text-align: left;

    }
    .icon-section {
        width: 20%;
        display: flex;
        justify-content: center;
    }
</style>
